<template>
  <main class="home-concept-page" data-image-enlarge="off" :class="{ 'is-engaged': engaged, 'is-scene-masked': sceneMasked, 'home-entrance-pending': entrancePending }" :data-home-arrival="entranceMode">
    <nav class="story-rail" data-home-enter="rail" aria-label="Homepage chapters">
      <button v-for="(chapter, index) in chapters" :key="chapter.id" type="button" :aria-label="'Go to ' + chapter.label" :aria-current="activeChapter === chapter.id ? 'step' : undefined" @click="goChapter(chapter.id)">
        <span class="story-rail-label" aria-hidden="true"><span class="story-rail-label-section">{{ chapter.label }}</span><span class="story-rail-label-title">{{ chapter.title }}</span></span><span class="story-rail-mark" aria-hidden="true">{{ String(index + 1).padStart(2, '0') }}</span>
      </button>
    </nav>

    <section id="story-hello" class="story-hero story-chapter" data-story-chapter="Hello" :aria-labelledby="mobileViewport ? 'concept-mobile-name' : 'concept-name'">
      <div class="concept-stage">
      <div class="concept-frame">
        <figure class="concept-scene" data-home-enter="scene">
          <div class="concept-media" :class="{ 'is-video-ready': sceneReady }">
            <img class="concept-video-poster" src="/assets/img/home5-first-frame.webp" width="607" height="1080" alt="" aria-hidden="true" decoding="async" fetchpriority="high">
            <video ref="sceneVideo" class="concept-video" poster="/assets/img/home5-first-frame.webp" width="607" height="1080" :muted="sceneMuted" playsinline webkit-playsinline preload="metadata" aria-label="The coast at Warrnambool" @volumechange="syncSceneSound">
              <source src="/assets/vid/home5.mp4" type="video/mp4">
            </video>
            <button v-if="!mobileViewport" type="button" class="concept-scene-sound" data-press-intent="custom" :aria-label="sceneMuted ? 'Play video sound' : 'Mute video'" :aria-pressed="!sceneMuted" @click="toggleSceneSound">
              <span>Pied cormorants resting along the sea cliffs of Warrnambool.</span>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M4.25 9.25h3.2l4.15-3.45v12.4l-4.15-3.45h-3.2z" />
                <g fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <path v-if="sceneMuted" d="M5.1 5.1 18.9 18.9" />
                  <path v-else d="M14.4 9.1c.72.77 1.08 1.73 1.08 2.9s-.36 2.13-1.08 2.9M17.25 6.85A7.35 7.35 0 0 1 19.1 12a7.35 7.35 0 0 1-1.85 5.15" />
                </g>
              </svg>
            </button>
            <div class="concept-scene-mask" :class="{ 'is-visible': sceneMasked }" aria-hidden="true"><span>{{ mobileViewport ? 'Shot in Warrnambool' : 'Shot in Warrnambool, Australia' }}&nbsp;&nbsp;06/2025</span></div>
          </div>
        </figure>

        <div class="concept-mobile-identity">
          <h1 id="concept-mobile-name"><span data-home-enter="name">Henry</span><span data-home-enter="name">Wang</span></h1>
          <div class="concept-mobile-intro">
            <HomeCreed />
            <figure class="concept-portrait" data-home-enter="portrait">
              <div class="concept-portrait-image"><img src="/assets/img/henry-rooftop-portrait.jpg" alt="Henry Wang" decoding="async"></div>
              <figcaption class="concept-portrait-caption">
                <HomeWelcomeAudio :active="!engaged" @play="muteSceneForWelcome" />
              </figcaption>
            </figure>
          </div>
          <nav class="concept-context" aria-label="Current affiliations">
            <router-link data-home-enter="context" :to="{ path: '/education', hash: '#study-hong-kong' }" aria-label="Read about Applied Artificial Intelligence at HKU"><strong>HKU</strong><span>Applied AI</span></router-link>
            <router-link data-home-enter="context" :to="{ path: '/experience', hash: '#work-pwc' }" aria-label="Read about Henry's role at PwC"><strong>PwC</strong><span>Cloud, Data &amp; AI</span></router-link>
          </nav>
        </div>
        <Teleport defer to="#story-mobile-ask" :disabled="!mobileViewport">
        <div class="concept-card" :data-story-reveal="mobileViewport ? '' : null">
          <header class="concept-card-header">
            <button v-if="!mobileViewport" type="button" class="concept-return" :inert="!engaged ? '' : null" :aria-hidden="!engaged" aria-label="Return to the introduction" @click="resetAsk"><span aria-hidden="true">←</span> Back</button>
          </header>
          <div class="concept-card-body">
            <div class="concept-intro" :inert="engaged ? '' : null" :aria-hidden="engaged">
              <template v-if="!mobileViewport">
              <section class="concept-identity">
                <h1 id="concept-name"><span data-home-enter="name">Henry</span><span data-home-enter="name">Wang</span></h1>
                <HomeCreed />
              </section>
              <div class="concept-portrait-note">
                <figure class="concept-portrait" data-home-enter="portrait">
                  <div class="concept-portrait-image"><img src="/assets/img/henry-rooftop-portrait.jpg" alt="Henry Wang" decoding="async"></div>
                  <figcaption class="concept-portrait-caption">
                    <HomeWelcomeAudio :active="!engaged" @play="muteSceneForWelcome" />
                  </figcaption>
                </figure>
              </div>
              </template>
              <div v-else class="concept-mobile-ask-intro"><h2><AiAskTerm /> me anything</h2><p>My work, my research, or life outside it.</p></div>
            </div>
            <section class="concept-answer" aria-label="Answer" :aria-busy="askBusy" :inert="!engaged ? '' : null" :aria-hidden="!engaged">
              <Transition name="concept-turn" mode="out-in">
              <div :key="askTurn" ref="answerScroll" class="concept-answer-scroll" @click.capture="rememberAnswerNavigation" tabindex="0" role="region" aria-label="Answer and sources">
                <h2 v-if="submittedQuestion" class="concept-question">{{ submittedQuestion }}</h2>
                <Transition name="concept-response" mode="out-in">
                <div v-if="askBusy && !askAnswerText" key="thinking" class="concept-thinking" role="status"><span aria-hidden="true"></span><p>Finding relevant parts of the site</p></div>
                <p v-else-if="askAnswerText" key="answer" class="concept-answer-copy" v-html="askAnswerHtml"></p>
                <p v-else-if="askError" key="error" class="concept-answer-error" role="alert">{{ askErrorText }}</p>
                </Transition>
                <AskAnswerAudio v-if="askAnswerText && !askBusy" :text="askAnswerText" />
                <AskActions v-if="askActionsList.length && askAnswerText && !askBusy" :actions="askActionsList" surface="home" />
                <ViewComposeProgress v-if="engaged && homeViewBusy" surface="home" :started-at="homeViewStartedAt" />
                <p v-if="homeViewError && !homeViewBusy" class="concept-answer-error" role="alert">{{ homeViewError }}</p>
                <AskViewOffer v-if="!homeViewBusy && (viewRouting === 'offer' || canReturnToView) && askAnswerText && !askBusy && !askError" :ready="canReturnToView" :title="viewOffer.title" :description="viewOffer.description" @explore="createViewFromAnswer" />
                <div v-if="citedSources.length && !askBusy" class="concept-answer-sources-wrap">
                  <span class="concept-answer-sources-label">Sources</span>
                  <TransitionGroup appear tag="div" class="concept-answer-sources" @before-enter="beforeHomeChipEnter" @enter="enterHomeChip" @leave="leaveHomeChip">
                    <button v-for="source in citedSources" :key="source.id" :data-source-key="source.id" type="button" class="concept-answer-source ask-source-link" :class="{ 'is-previewed': hoveredSourceId === source.id }" @mouseenter="hoveredSourceId = source.id" @mouseleave="hoveredSourceId = null" @focus="hoveredSourceId = source.id" @blur="hoveredSourceId = null" @click="goSource(source)">{{ source.page }} › {{ source.title }}</button>
                  </TransitionGroup>
                </div>
                <div v-if="followUps.length && askAnswerText && !askBusy" class="concept-answer-followups">
                  <span class="concept-answer-followups-label">Keep exploring</span>
                  <div class="concept-answer-followups-list"><button v-for="question in followUps" :key="question" type="button" class="concept-answer-followup ask-followup-chip" :disabled="askInputLocked" @click="askFollowUp(question)">{{ question }}</button></div>
                </div>
                <div v-if="askMapVisible || askBusy" class="home-ask-map-shell">
                  <EmbeddingMap ref="homeAskMap" class="home-ask-map" compact fit-stage initial-view-mode="3d" :monochrome="true" :show-controls="false" :show-view-toggle="false" :show-caption="false" :show-hits="false" :allow-navigation="!askBusy && (!!askAnswerText || !!askError)" two-finger-touch wheel-zoom-requires-pinch @ready="syncHomeAskMap" />
                </div>
              </div>
              </Transition>
            </section>
          </div>
          <form class="concept-ask" :data-home-enter="mobileViewport ? null : 'ask'" :class="{ 'is-busy': askBusy, 'is-compact': engaged && !askInputActive }" @submit.prevent="submitAsk">
            <label class="concept-sr-only" for="concept-ask-input">Ask this site about Henry</label>
            <div class="concept-ask-field" @click.self="focusAskInput">
              <textarea v-if="mobileViewport" id="concept-ask-input" ref="askInput" v-model="askText" :disabled="askInputLocked" rows="2" autocomplete="off" placeholder="Your question" @focus="askInputActive = true" @blur="askInputActive = false" @compositionstart="askComposing = true" @compositionend="askComposing = false"></textarea>
              <input v-else id="concept-ask-input" ref="askInput" v-model="askText" :disabled="askInputLocked" type="text" autocomplete="off" :placeholder="engaged ? 'Ask me another question' : 'Ask a question ...'" :aria-label="engaged ? 'Ask a follow-up question' : 'Ask this site about Henry'" @compositionstart="askComposing = true" @compositionend="askComposing = false">
              <button type="submit" :disabled="!askText.trim() || askInputLocked" :aria-label="homeViewBusy ? 'Composing your View' : askBusy ? 'Finding an answer' : 'Submit question'" @pointerdown="keepMobileAskFocus">
                <svg v-if="mobileViewport" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 18V6m-5 5 5-5 5 5" /></svg>
                <span v-else aria-hidden="true">→</span>
              </button>
            </div>
          </form>
        </div>
        </Teleport>
      </div>
      </div>
      <button type="button" class="story-scroll-cue" data-home-enter="cue" aria-label="Continue to the next chapter" @click="goChapter('story-now')"><span class="story-next-label">02 / Now</span><span class="story-next-title">What I’m working on</span></button>
    </section>

    <section id="story-mobile-ask" class="story-mobile-ask" aria-label="Ask about Henry"></section>

    <section id="story-now" class="story-chapter story-now" data-story-chapter="Now" aria-labelledby="story-now-heading">
      <div class="story-now-heading" data-story-reveal><p class="story-eyebrow">02 / Now</p><h2 id="story-now-heading">What I’m<br>working on</h2></div>
      <nav class="concept-current" aria-label="Current focus">
        <router-link :to="{ path: '/projects', hash: '#searchstory' }" data-story-reveal><span class="concept-current-number" aria-hidden="true">01</span><span class="concept-current-copy"><small>Ongoing project</small><strong>AI × Self</strong></span><ArrowUpRightIcon /></router-link>
        <router-link :to="{ path: '/research', hash: '#structure-settles-thought' }" data-story-reveal><span class="concept-current-number" aria-hidden="true">02</span><span class="concept-current-copy"><small>Research in progress</small><strong>Early Epistemic Settlement</strong></span><ArrowUpRightIcon /></router-link>
        <router-link :to="{ path: '/travels', hash: '#hong-kong' }" data-story-reveal><span class="concept-current-number" aria-hidden="true">03</span><span class="concept-current-copy"><small>Current location</small><strong>Hong Kong</strong></span><ArrowUpRightIcon /></router-link>
      </nav>
    </section>

    <AboutTab story />

    <section id="story-index" class="story-chapter story-index" data-story-chapter="Index" aria-labelledby="story-index-heading">
      <header data-story-reveal>
        <div><p class="story-eyebrow">08 / Index</p><h2 id="story-index-heading">Index</h2></div>
        <button type="button" class="story-index-home" @click="goChapter('story-hello')">top <span aria-hidden="true">↑</span></button>
      </header>
      <nav aria-label="Site index">
        <router-link v-for="item in siteIndexItems" :key="item.route" :to="item.route" data-story-reveal>
          <span>{{ item.label }}</span>
        </router-link>
      </nav>
    </section>

    <Teleport to="body">
      <Transition name="home-preview-fade">
        <div v-if="sourcePreview" :key="sourcePreview.id" class="home-ask-preview" :style="previewStyle" aria-hidden="true">
          <span class="home-ask-preview-kicker">{{ sourcePreview.page }} › {{ sourcePreview.title }}</span>
          <p class="home-ask-preview-text">{{ sourcePreview.snippet }}</p>
        </div>
      </Transition>
    </Teleport>
  </main>
</template>

<script>
import AskViewOffer from './ui/AskViewOffer.vue';
import ViewComposeProgress from './view/ViewComposeProgress.vue';
import { isViewComposing, viewComposeState } from '../utils/viewSite.js';
import { loadHomeAskSnapshot, saveHomeAskSnapshot, prepareAskSourceReturn, consumeAskSourceDeparture } from '../utils/askReturn.js';
import { watchDesktopHover } from '../utils/desktopHover';
import { askConfigured, askErrorMessage, askStream } from '../utils/askSite';
import {
  allAskContexts,
  askAnswerFollowUps,
  askViewOffer,
  askViewRouting,
  groundAskFollowUps,
  looksLikeViewBuildRequest,
  askResponseIsComplete,
  citedSourcesFromAnswer,
  cleanAskAnswer,
  cleanAskContextText,
  diversifyAskWritingContexts,
  sourceDisplayTitle,
} from '../utils/askRetrieval';
import { askActionsForDisplay, renderAskAnswerLinks } from '../utils/askActions';
import { newAskContext } from '../utils/askTelemetry';
import { addAskTurn, askSourceIdsInRecent, loadAskSession, matchingAskView } from '../utils/askSession';
import { cancelAnchorLanding, settleScrollToId } from '../utils/anchorLanding';
import { sourceRouteTarget } from '../utils/viewSourceTarget';
import AskAnswerAudio from './ui/AskAnswerAudio.vue';
import AskActions from './ui/AskActions.vue';
import ArrowUpRightIcon from './ui/ArrowUpRightIcon.vue';
import AiAskTerm from './ui/AiAskTerm.vue';
import HomeCreed from './ui/HomeCreed.vue';
import HomeWelcomeAudio from './ui/HomeWelcomeAudio.vue';
import EmbeddingMap from './EmbeddingMap.vue';
import { semanticTopK } from '../utils/semanticSearch';
import { animate } from 'motion';
import AboutTab from './AboutTab.vue';
import { initHomeStoryMotion } from '../utils/homeStoryMotion';
import { initHomeScene } from '../utils/homeScene';
import { revealVideoOnFirstFrame } from '../utils/videoFirstFrame';
import { claimHomeEntrance, consumeHomeIntroduction, initHomeEntrance } from '../utils/homeEntrance';

export default {
  name: 'HomeConceptPage',
  components: { ViewComposeProgress, AskViewOffer, ArrowUpRightIcon, AskActions, AskAnswerAudio, AiAskTerm, HomeCreed, HomeWelcomeAudio, AboutTab, EmbeddingMap },
  data() {
    const replayIntroduction = consumeHomeIntroduction(window.location.hash);
    // A fresh introduction leaves the saved answer available for an ordinary
    // Home return, without letting its expanded layout replace the hero.
    const snapshot = replayIntroduction ? null : loadHomeAskSnapshot();
    const entranceMode = claimHomeEntrance({
      hash: window.location.hash, initialHash: window.__personalPageInitialHash, chapter: this.$route.hash,
      replay: replayIntroduction,
      restored: Boolean(snapshot?.state?.askOpen),
      reduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
    return {
      replayIntroduction, entranceMode, entrancePending: entranceMode !== 'settled',
      mobileViewport: window.matchMedia('(max-width: 760px)').matches,
      desktopHover: false,
      askOpen: false,
      askInputActive: false,
      askTurn: 0,
      askText: '',
      submittedQuestion: '',
      askAnswer: '',
      askSources: [],
      askBusy: false,
      askError: '',
      askComposing: false,
      sceneMasked: false,
      sceneReady: false,
      sceneMuted: true,
      viewRouting: 'none',
      homeViewError: '',
      viewSession: loadAskSession(),
      groundedFollowUps: null,
      askMapVisible: false,
      askMapHits: [],
      askMapLabel: '',
      askMapFinalized: false,
      hoveredSourceId: null,
      previewStyle: null,
      activeChapter: 'story-hello',
      siteIndexItems: [
        { label: 'Study', route: '/education' },
        { label: 'Work', route: '/experience' },
        { label: 'Research', route: '/research' },
        { label: 'Builds', route: '/projects' },
        { label: 'Fragments', route: '/fragments' },
        { label: 'Views', route: '/views' },
      ],
      chapters: [
        { id: 'story-hello', label: 'Hello', title: 'Meet Henry' },
        { id: 'story-now', label: 'Now', title: 'What I’m working on' },
        { id: 'story-about', label: 'About', title: 'A little about me' },
        { id: 'story-research', label: 'Research', title: 'How we learn and think' },
        { id: 'story-builds', label: 'Builds', title: 'Things I build' },
        { id: 'story-life', label: 'Life', title: 'Outside work' },
        { id: 'story-contact', label: 'Contact', title: 'Say hello' },
        { id: 'story-index', label: 'Index', title: 'Site index' },
      ],
      ...(snapshot?.state || {}),
    };
  },
  computed: {
    engaged() { return this.askOpen; },
    homeViewBusy() { return viewComposeState.active && viewComposeState.surface === 'home' && viewComposeState.label === this.submittedQuestion; },
    homeViewStartedAt() { return viewComposeState.startedAt; },
    askInputLocked() { return this.askBusy || isViewComposing(); },
    askAnswerText() { return cleanAskAnswer(this.askAnswer); },
    askAnswerHtml() { return renderAskAnswerLinks(this.askAnswerText); },
    askActionsList() { return askActionsForDisplay(this.askAnswer, this.viewRouting); },
    askErrorText() { return askErrorMessage(); },
    citedSources() {
      const seen = new Set();
      return citedSourcesFromAnswer(this.askAnswer, this.askSources).filter(source => {
        const key = `${source.route}#${source.anchor || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }).map(source => ({ ...source, title: sourceDisplayTitle(source) }));
    },
    followUps() { return this.groundedFollowUps ?? askAnswerFollowUps(this.askAnswer).slice(0, 3); },
            sourcePreview() {
                if (!this.desktopHover || !this.hoveredSourceId) return null;
                const s = this.citedSources.find((x) => x.id === this.hoveredSourceId);
                if (!s) return null;
                const text = cleanAskContextText(s.body || '').replace(/\s+/g, ' ').trim();
                const snippet = text
                    ? (text.length > 230 ? `${text.slice(0, 230).trim()}…` : text)
                    : 'Open the passage to read it.';
                return { id: s.id, page: s.page, title: s.title, snippet };
            },
    viewOffer() { return askViewOffer(this.askAnswer); },
    canReturnToView() { return !!matchingAskView(this.submittedQuestion, this.viewSession); },
  },
  watch: {
    hoveredSourceId(id) { this.positionSourcePreview(id); },
  },
  mounted() {
    // The outgoing page has now left the DOM. Reset before the first paint and
    // before the entrance checks scroll, never while the old page is visible.
    if (this.replayIntroduction) window.scrollTo({ top: 0, behavior: 'instant' });
    this._onAskSessionUpdated = event => { this.viewSession = event.detail; };
    window.addEventListener('ask-session-updated', this._onAskSessionUpdated);
    this._onHomeViewError = event => { this.homeViewError = String(event.detail?.message || 'Please try again.'); };
    window.addEventListener('home-view-error', this._onHomeViewError);
    if (!this.replayIntroduction) this.restoreAnswerPosition();
    this._stopHover = watchDesktopHover(enabled => { this.desktopHover = enabled; if (!enabled) this.hoveredSourceId = null; });
    this._dismissSourcePreview = () => { this.hoveredSourceId = null; };
    window.addEventListener('scroll', this._dismissSourcePreview, { capture: true, passive: true });
    this._askSequence = 0;
    this._mobileQuery = window.matchMedia('(max-width: 760px)');
    this._updateMobile = () => {
      this.mobileViewport = this._mobileQuery.matches;
      if (this.mobileViewport && this.$refs.sceneVideo) {
        this.$refs.sceneVideo.muted = true;
        this.sceneMuted = true;
      }
    };
    this._updateMobile();
    this._mobileQuery.addEventListener('change', this._updateMobile);
    this.$nextTick(() => {
      this._stopEntrance = initHomeEntrance(this.$el, {
        mode: this.engaged ? 'settled' : this.entranceMode,
        onReady: () => { this.entrancePending = false; },
      });
      this._stopStoryMotion = initHomeStoryMotion(this.$el, (id) => { this.activeChapter = id; });
      const header = document.querySelector('.container > .header');
      this._syncChapterViewport = () => {
        const fixed = header && ['fixed', 'sticky'].includes(window.getComputedStyle(header).position);
        const inset = fixed ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        this.$el.style.setProperty('--story-top', `${inset}px`);
      };
      this._syncChapterViewport();
      window.addEventListener('resize', this._syncChapterViewport);
      if (header) {
        this._chapterHeaderResize = new ResizeObserver(this._syncChapterViewport);
        this._chapterHeaderResize.observe(header);
      }
      const footer = document.querySelector('.container > .footer');
      if (footer) {
        this._footerResize = new ResizeObserver(() => {
          const style = window.getComputedStyle(footer);
          const space = footer.offsetHeight + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
          this.$el.style.setProperty('--story-footer-space', `${space}px`);
        });
        this._footerResize.observe(footer, { box: 'border-box' });
      }
    });
    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._onMotionChange = () => {
      if (this._reducedMotion.matches) this.$refs.sceneVideo?.pause();
    };
    this._reducedMotion.addEventListener('change', this._onMotionChange);
    this._stopFirstFrame = revealVideoOnFirstFrame(this.$refs.sceneVideo, () => { this.sceneReady = true; });
    this._stopScene = initHomeScene(this.$refs.sceneVideo, {
      onMask: active => { this.sceneMasked = active; },
      canPlay: () => {
        const bounds = this.$refs.sceneVideo.getBoundingClientRect();
        return !document.hidden && !this._reducedMotion.matches && bounds.bottom > 0 && bounds.top < window.innerHeight;
      },
    });
    if (!this._reducedMotion.matches) this.$refs.sceneVideo?.play().catch(() => {});
  },
  beforeRouteLeave() {
    this.saveAnswerPosition();
  },
  beforeUnmount() {
    window.removeEventListener('ask-session-updated', this._onAskSessionUpdated);
    window.removeEventListener('home-view-error', this._onHomeViewError);
    this._stopHover?.();
    // Teleport removes the mobile Ask contents before the page fade finishes.
    // Reserve their height until the outgoing homepage itself is removed.
    const mobileAsk = this.$el.querySelector('#story-mobile-ask');
    if (this.mobileViewport && mobileAsk) mobileAsk.style.minHeight = `${mobileAsk.getBoundingClientRect().height}px`;
    this._askSequence += 1;
    window.removeEventListener('scroll', this._dismissSourcePreview, { capture: true });
    this._homeAnims?.forEach(animation => animation.stop());
    this._stopEntrance?.();
    this._stopStoryMotion?.();
    this._stopChapterScroll?.();
    this._stopScene?.();
    this._stopFirstFrame?.();
    this._footerResize?.disconnect();
    this._chapterHeaderResize?.disconnect();
    window.removeEventListener('resize', this._syncChapterViewport);
    this._mobileQuery?.removeEventListener('change', this._updateMobile);
    this._reducedMotion?.removeEventListener('change', this._onMotionChange);
  },
  methods: {
            positionSourcePreview(id) {
                if (!id) {
                    this.previewStyle = null;
                    return;
                }
                this.$nextTick(() => {
                    const wrap = this.$el && this.$el.querySelector('.concept-answer-sources-wrap');
                    const chip = wrap && wrap.querySelector(`.concept-answer-source[data-source-key="${id}"]`);
                    const preview = document.querySelector('.home-ask-preview');
                    if (!wrap || !chip || !preview) return;
                    const cr = chip.getBoundingClientRect();
                    const gap = 8;
                    const width = Math.max(200, Math.min(340, window.innerWidth - 24));
                    const left = Math.max(12, Math.min(cr.left, window.innerWidth - width - 12));
                    const height = preview.offsetHeight || 150;
                    const vh = window.innerHeight;
                    let top;
                    if (cr.bottom + gap + height <= vh - 8) {
                        top = cr.bottom + gap;
                    } else if (cr.top - gap - height >= 8) {
                        top = cr.top - gap - height;
                    } else {
                        top = Math.max(8, vh - height - 8);
                    }
                    this.previewStyle = {
                        left: `${left}px`,
                        top: `${top}px`,
                        width: `${width}px`,
                    };
                });
            },
            beforeHomeChipEnter(el) {
                el.style.opacity = '0';
                el.style.transform = 'translateY(4px)';
            },
            enterHomeChip(el, done) {
                if (this.homeReduceMotion()) {
                    el.style.opacity = '';
                    el.style.transform = '';
                    done();
                    return;
                }
                const index = Math.max(0, Array.from(el.parentNode ? el.parentNode.children : []).indexOf(el));
                let settled = false;
                const finish = () => {
                    if (settled) return;
                    settled = true;
                    el.style.opacity = '';
                    el.style.transform = '';
                    this.untrackHomeAnim(animation);
                    done();
                };
                const animation = this.trackHomeAnim(animate(el, {
                    opacity: [0, 1],
                    transform: ['translateY(4px)', 'translateY(0px)'],
                }, {
                    type: 'spring',
                    bounce: 0,
                    duration: 0.3,
                    delay: index * 0.045,
                    onComplete: finish,
                }));
                window.setTimeout(finish, 1000);
            },
            leaveHomeChip(el, done) {
                if (this.homeReduceMotion()) {
                    done();
                    return;
                }
                let settled = false;
                const finish = () => {
                    if (settled) return;
                    settled = true;
                    this.untrackHomeAnim(animation);
                    done();
                };
                const animation = this.trackHomeAnim(animate(el, {
                    opacity: [1, 0],
                    transform: ['translateY(0px)', 'translateY(-2px)'],
                }, {
                    type: 'spring',
                    bounce: 0,
                    duration: 0.12,
                    onComplete: finish,
                }));
                window.setTimeout(finish, 400);
            },
    homeReduceMotion() { return this._reducedMotion?.matches; },
    trackHomeAnim(animation) { (this._homeAnims ||= new Set()).add(animation); return animation; },
    untrackHomeAnim(animation) { this._homeAnims?.delete(animation); },
            async prepareHomeAskMap(question, seq) {
                try {
                    const hits = await semanticTopK(question, 8);
                    if (seq !== this._askSequence || this.askMapFinalized) return;
                    this.askMapHits = hits.map((h) => ({ id: h.id, score: h.score }));
                    this.askMapLabel = question;
                    const map = this.$refs.homeAskMap;
                    if (map && map.showExternalHits) {
                        // While waiting for the answer: keep the full map in view and just
                        // light up the retrieved passages, no zoom-in yet.
                        map.showExternalHits(this.askMapHits, {
                            label: question,
                            focus: false,
                            keepCentered: false,
                            duration: 900,
                        });
                    }
                } catch (err) {
                    console.warn('home ask map failed:', err);
                }
            },
            citedSourceItems() {
                const cited = new Set();
                const re = /\[([\d,\s]+)\]/g;
                let m;
                while ((m = re.exec(this.askAnswer))) {
                    m[1].split(/[\s,]+/).filter(Boolean).forEach((n) => cited.add(parseInt(n, 10)));
                }
                return this.askSources.filter((source, i) => cited.has(i + 1) && source && source.id != null);
            },
            finishHomeAskMap() {
                const hits = this.citedSourceItems().map((source) => ({ id: source.id, score: 0.92 }));
                this.askMapHits = hits;
                this.askMapLabel = hits.length ? 'cited passages' : this.askMapLabel;
                this.askMapFinalized = true;
                const map = this.$refs.homeAskMap;
                if (!map || !map.showExternalHits) return;
                // Answer is in: zoom in onto the cited passages. (With no citations the
                // hit list is empty and the map falls back to the full view.)
                map.showExternalHits(hits, {
                    label: this.askMapLabel,
                    focus: true,
                    keepCentered: true,
                    duration: 850,
                });
            },
            syncHomeAskMap() {
                const map = this.$refs.homeAskMap;
                if (!map || !map.showExternalHits || !this.askMapHits.length) return;
                map.showExternalHits(this.askMapHits, {
                    label: this.askMapLabel,
                    // Full map until the answer is finalized, then focus on the cited passages.
                    focus: this.askMapFinalized,
                    keepCentered: this.askMapFinalized,
                    duration: 500,
                });
            },
    muteSceneForWelcome() {
      if (this.$refs.sceneVideo) this.$refs.sceneVideo.muted = true;
      this.sceneMuted = true;
    },
    syncSceneSound() {
      if (this.$refs.sceneVideo) this.sceneMuted = this.$refs.sceneVideo.muted;
    },
    toggleSceneSound() {
      const video = this.$refs.sceneVideo;
      if (!video || this.mobileViewport) return;
      video.muted = !video.muted;
      this.sceneMuted = video.muted;
      if (!video.muted) window.dispatchEvent(new CustomEvent('view-summary-play', { detail: this }));
    },
    createViewFromAnswer() {
      if (!this.submittedQuestion || isViewComposing()) return;
      this.askOpen = true;
      this.homeViewError = '';
      this.saveAnswerPosition();
      if (matchingAskView(this.submittedQuestion, loadAskSession())) {
        this.$router.push({ path: '/view' }).catch(() => {});
        return;
      }
      window.dispatchEvent(new CustomEvent('open-site-view', {
        detail: {
          question: this.submittedQuestion, compose: true, askOfferConfirmed: true, surface: 'home',
          returnContext: { pageY: window.scrollY, answerTop: this.$refs.answerScroll?.scrollTop || 0 },
        },
      }));
    },
    async groundFollowUps(sequence) {
      const questions = askAnswerFollowUps(this.askAnswer).slice(0, 3);
      const grounded = await groundAskFollowUps(questions);
      if (sequence === this._askSequence) this.groundedFollowUps = grounded;
    },
    goChapter(id) {
      const target = this.$el.querySelector(`#${id}`);
      if (!target) return;
      cancelAnchorLanding();
      this._stopChapterScroll?.();
      this._syncChapterViewport?.();
      const destination = () => {
        if (id === 'story-hello') return 0;
        const inset = parseFloat(window.getComputedStyle(this.$el).getPropertyValue('--story-top')) || 0;
        const divider = parseFloat(window.getComputedStyle(target).borderTopWidth) || 0;
        // Land inside the chapter, past its separator. Round upward so a
        // fractional document coordinate cannot leave a sliver of the line.
        return Math.max(0, Math.ceil(window.scrollY + target.getBoundingClientRect().top + divider - inset));
      };
      if (this._reducedMotion?.matches) {
        window.scrollTo({ top: destination(), behavior: 'instant' });
        return;
      }
      const start = window.scrollY;
      const duration = Math.min(600, Math.max(360, Math.abs(destination() - start) * .12));
      let frame;
      let started;
      const stop = () => {
        cancelAnimationFrame(frame);
        ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(type => window.removeEventListener(type, stop));
        if (this._stopChapterScroll === stop) this._stopChapterScroll = null;
      };
      this._stopChapterScroll = stop;
      ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(type => window.addEventListener(type, stop, { passive: true }));
      const step = time => {
        if (started === undefined) started = time;
        const progress = Math.min(1, (time - started) / duration);
        const end = destination();
        window.scrollTo({ top: progress === 1 ? end : start + (end - start) * (1 - Math.pow(1 - progress, 3)), behavior: 'instant' });
        if (progress < 1) frame = requestAnimationFrame(step);
        else stop();
      };
      frame = requestAnimationFrame(step);
    },
    focusAskInput() {
      this.$refs.askInput?.focus();
    },
    keepMobileAskFocus(event) {
      // Keep the send target still until the tap submits the question.
      if (this.mobileViewport) event.preventDefault();
    },
    submitAsk() {
      if (this.askComposing) return;
      const question = this.askText.trim();
      if (!question || this.askInputLocked) return;
      this.runAsk(question);
    },
    askFollowUp(question) {
      this.runAsk(question);
      if (!this.mobileViewport) this.$nextTick(() => this.$refs.askInput?.focus());
    },
    resetAsk() {
      saveHomeAskSnapshot(null);
      this._askSequence += 1;
      this.askText = '';
      // Keep the outgoing answer mounted while the card returns to its introduction.
      this.askOpen = false;
      this.askBusy = false;
      this.askMapVisible = false;
      this.hoveredSourceId = null;
      this.$nextTick(() => this.$refs.askInput?.focus());
    },
    async runAsk(question) {
      if (isViewComposing()) return;
      if (looksLikeViewBuildRequest(question)) {
        this.askAnswer = '';
        this.askSources = [];
        this.submittedQuestion = question;
        this.createViewFromAnswer();
        return;
      }
      const sequence = ++this._askSequence;
      const telemetry = newAskContext('home');
      if (this.mobileViewport) this.$refs.askInput?.blur();
      this.askInputActive = false;
      this.askOpen = true;
      this.askTurn += 1;
      this.submittedQuestion = question;
      this.askText = '';
      this.askAnswer = '';
      this.askSources = [];
      this.askError = '';
      this.viewRouting = 'none';
      this.groundedFollowUps = null;
      this.askMapVisible = true;
      this.askMapHits = [];
      this.askMapLabel = question;
      this.askMapFinalized = false;
      this.hoveredSourceId = null;
      this.askBusy = true;
      this.$nextTick(() => this.prepareHomeAskMap(question, sequence));
      this.$nextTick(() => {
        if (this.$refs.answerScroll) this.$refs.answerScroll.scrollTop = 0;
      });
      try {
        if (!askConfigured()) throw new Error('ask unavailable');
        const recentSourceIds = askSourceIdsInRecent(loadAskSession(), 18);
        const contexts = diversifyAskWritingContexts(await allAskContexts(question), question, telemetry.event_id, recentSourceIds);
        if (sequence !== this._askSequence) return;
        this.askSources = contexts;
        const payload = contexts.map((context) => ({
          page: context.page,
          title: sourceDisplayTitle(context),
          text: cleanAskContextText(context.body),
          source_id: context.source_id || context.chunk_id || String(context.id || ''),
        }));
        const stream = askStream(question, payload, {
          meta: {
            event_id: telemetry.event_id,
            session_id: telemetry.session_id,
            turn_index: telemetry.turn_index,
            surface: telemetry.surface,
            recent_source_ids: recentSourceIds,
          },
        });
        let answer = '';
        for await (const token of stream) {
          if (sequence !== this._askSequence) return;
          answer += token;
          this.askAnswer = answer;
        }
        if (!askResponseIsComplete(answer)) throw new Error('incomplete ask response');
        if (sequence !== this._askSequence) return;
        this.viewRouting = askViewRouting(answer);
        addAskTurn(addAskTurn(loadAskSession(), {
          role: 'user', text: question, source_ids: [],
        }), {
          role: 'assistant', text: answer,
          source_ids: citedSourcesFromAnswer(answer, contexts).map(source => source.source_id).filter(Boolean),
        });
        this.finishHomeAskMap();
        if (this.viewRouting === 'build') this.createViewFromAnswer();
        this.groundFollowUps(sequence).catch(() => {});
      } catch (error) {
        if (sequence !== this._askSequence) return;
        console.error('home ask failed:', error);
        this.askAnswer = '';
        this.askSources = [];
        this.askError = String(error?.message || error);
      } finally {
        if (sequence === this._askSequence) this.askBusy = false;
      }
    },
    saveAnswerPosition() {
      if (!this.askOpen || !this.submittedQuestion || this.askBusy) return;
      const state = {};
      for (const key of ['askOpen', 'askTurn', 'askText', 'submittedQuestion', 'askAnswer', 'askSources', 'askError', 'viewRouting', 'groundedFollowUps', 'askMapVisible', 'askMapHits', 'askMapLabel', 'askMapFinalized']) state[key] = this[key];
      saveHomeAskSnapshot({ state, answerTop: this.$refs.answerScroll?.scrollTop || 0, pageY: window.scrollY });
    },
    restoreAnswerPosition() {
      const snapshot = loadHomeAskSnapshot();
      if (!snapshot) return;
      Object.assign(this, snapshot.state);
      this.$nextTick(() => {
        if (this.$refs.answerScroll) this.$refs.answerScroll.scrollTop = snapshot.answerTop;
        this.syncHomeAskMap();
      });
    },
    rememberAnswerNavigation(event) {
      if (event.target.closest('.ask-inline-link, .ask-action[data-ask-navigate]')) this.rememberSourceReturn();
    },
    rememberSourceReturn() {
      this.saveAnswerPosition();
      prepareAskSourceReturn({ route: this.$route.fullPath, label: 'Ask', scrollY: window.scrollY,
        askReturn: { surface: 'home' } });
    },
    goSource(source) {
      this.hoveredSourceId = null;
      if (!source?.route) return;
      const destination = sourceRouteTarget(source.route, source.anchor);
      if (destination?.href) {
        window.open(destination.href, '_blank', 'noopener');
        return;
      }
      this.rememberSourceReturn();
      if (this.$route.path !== source.route) {
        const target = { path: source.route };
        if (source.anchor) target.hash = `#${source.anchor}`;
        this.$router.push(target).catch(() => {});
        return;
      }
      consumeAskSourceDeparture();
      if (source.anchor) settleScrollToId(source.anchor);
    },
  },
};
</script>

<style scoped>
.home-concept-page {
  --story-top: 0px;
  --story-page-height: calc(100svh - var(--story-top));
  --home-muted: #76767c;
  --home-hairline: rgba(36,36,38,0.16);
  --ink: #242426;
  --muted: #68686c;
  --faint: #77777b;
  --line: rgba(36, 36, 38, 0.16);
  --surface: #f7f7f5;
  --page-background: #fff;
  --move: cubic-bezier(0.22, 1, 0.36, 1);
  width: min(calc(100% - 8rem), var(--home-page-measure, 78rem));
  margin: 0 auto;
  color: var(--ink);
  font-family: "Quattrocento Sans", sans-serif;
  container: concept / inline-size;
}
.home-concept-page *,
.home-concept-page *::before,
.home-concept-page *::after { box-sizing: border-box; }
.story-chapter { min-height: var(--story-page-height); scroll-margin-top: var(--story-top); }
.story-hero { min-height: calc(100svh - 6rem); display: flex; flex-direction: column; justify-content: center; gap: 2rem; padding: 2rem 0 1rem; }
.concept-stage {
  width: min(100%, var(--home-hero-measure, 64rem), var(--home-hero-height-measure, calc(max(24rem, 100svh - 13rem) * 1024 / 594)));
  margin-inline: auto;
  container: hero / inline-size;
}
.concept-frame {
  /* Scale the 1024 × 594 composition as one unit. Percentage grid tracks
     respond immediately to resizing while still animating the Ask state. */
  --composition-unit: calc(100cqw / 1024);
  --hero-unit: calc(var(--composition-unit) * 12.48);
  --hero-height: calc(var(--composition-unit) * 594);
  --scene-gap: calc(var(--composition-unit) * 24);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 32.62939453125%);
  align-items: center;
  gap: var(--scene-gap);
  width: 100%;
  height: var(--hero-height);
  margin-inline: auto;
  transition: grid-template-columns 650ms var(--move);
}
.concept-scene { position: relative; z-index: 0; isolation: isolate; grid-column: 2; grid-row: 1; display: flex; min-width: 0; height: 100%; flex-direction: column; align-items: center; justify-content: flex-end; margin: 0; }
.concept-media { position: relative; container-type: inline-size; width: 100%; aspect-ratio: 9 / 16; flex-shrink: 0; overflow: hidden; }
@media (min-width: 761px) {
  .concept-media {
    --home-white-hand: var(--site-white-interactive-cursor);
  }
  .concept-media,
  .concept-media :deep(*) { cursor: var(--home-white-hand) !important; }
}
.concept-scene-sound { position: absolute; inset: 0; z-index: 2; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 1rem; padding: clamp(1.25rem,8cqw,2rem); border: 0; background: rgba(17,17,17,0.5); color: #f5f5f7; font: 1rem/1.5 "Quattrocento Sans",sans-serif; text-align: center; opacity: 0; cursor: pointer; transition: opacity 260ms ease; }
.concept-scene-sound svg { width: 2.5rem; height: 2.5rem; flex-shrink: 0; }
.concept-scene-sound:focus-visible { opacity: 1; outline: 2px solid #f5f5f7 !important; outline-offset: -4px !important; }
.concept-video, .concept-video-poster { display: block; width: 100%; height: 100%; object-fit: contain; object-position: center; }
.concept-video { opacity: 0; }
.concept-video-poster { position: absolute; inset: 0; pointer-events: none; }
.concept-media.is-video-ready .concept-video { opacity: 1; }
.concept-media.is-video-ready .concept-video-poster { visibility: hidden; }
/* Keep the mask present for the entire return dissolve, including the slow
   start. Its exit curve should feel like film, not a quick UI dismissal. */
.concept-scene-mask { position: absolute; inset: 0; z-index: 3; display: grid; place-items: center; background: var(--page-background); color: var(--ink); opacity: 0; visibility: hidden; pointer-events: none; transition: opacity 1000ms cubic-bezier(0.42,0,0.58,1), visibility 0s 1000ms; }
.concept-scene-mask.is-visible { opacity: 1; visibility: visible; transition: opacity 420ms cubic-bezier(0.32,0.72,0,1), visibility 0s; }
.concept-scene-mask span { font: 500 clamp(0.65rem,3.6cqw,1.05rem)/1.3 -apple-system,BlinkMacSystemFont,"SF Pro Text",system-ui,sans-serif; letter-spacing: 0.08em; white-space: nowrap; opacity: 0; transform: rotate(90deg) translateY(8px); transition: opacity 260ms ease, transform 520ms var(--move); }
.concept-scene-mask.is-visible span { opacity: 0.82; transform: rotate(90deg) translateY(0); transition-delay: 180ms; }
.concept-card { grid-column: 1; grid-row: 1; position: relative; display: grid; grid-template-rows: minmax(0,1fr) auto; min-width: 0; height: 100%; padding: 0; border: 0; border-radius: 3px; background: transparent; z-index: 2; transition: padding 650ms var(--move), background-color 350ms ease; }
.concept-card-header { position: absolute; top: 1rem; right: 1.5rem; z-index: 1; }
.concept-return { display: flex; align-items: center; gap: 0.5rem; padding: 0.4rem 0; border: 0; background: transparent; color: inherit; font: inherit; font-size: 0.85rem; cursor: pointer; opacity: 0; visibility: hidden; transform: translateX(8px); transition: opacity 220ms ease, transform 350ms var(--move), visibility 0s 350ms; }
.concept-return span { display: inline-block; transition: transform 220ms var(--move); }
.concept-return:focus-visible span { transform: translateX(-4px); }
.concept-card-body { position: relative; min-height: 0; margin-block: calc(var(--composition-unit) * 32) calc(var(--composition-unit) * 24); }
.concept-intro { position: absolute; inset: 0; display: block; opacity: 1; visibility: visible; transform: translateY(0); transition: opacity 280ms ease 120ms, transform 550ms var(--move) 80ms, visibility 0s; }
.concept-identity { position: absolute; left: 0; bottom: calc(var(--composition-unit) * 8); display: flex; flex-direction: column; gap: calc(var(--composition-unit) * 50); }
.concept-identity h1 { display: grid; margin: 0; font-family: "Cormorant Garamond", Georgia, serif; font-size: calc(var(--composition-unit) * 144); font-weight: 500; letter-spacing: -0.06em; line-height: 0.83; }
.concept-credo { grid-column: 1; grid-row: 2; display: grid; gap: 0.15rem; margin: 0; font-family: -apple-system,BlinkMacSystemFont,"SF Pro Text","Helvetica Neue",sans-serif; font-size: clamp(1.3rem,calc(var(--hero-unit) * 2.7),2.1rem); line-height: 1.45; letter-spacing: -0.015em; }
@media (min-width: 761px) {
  .concept-credo { gap: calc(var(--composition-unit) * 4); font-size: calc(var(--composition-unit) * 35); line-height: 1.5; }
  .concept-portrait-note .concept-portrait { padding: calc(var(--composition-unit) * 6.4); }
}
.concept-context { grid-column: 1 / -1; display: flex; flex-direction: column; align-items: flex-start; gap: 0.65rem; margin-top: 0.5rem; }
.concept-context a { display: flex; align-items: baseline; gap: 0.7rem; color: var(--muted); text-decoration: none; transition: color 200ms ease, transform 260ms var(--move); }
.concept-context a span { font-size: 0.78rem; letter-spacing: 0.2em; text-transform: uppercase; }
.concept-context a strong { color: var(--ink); font-size: 1.08rem; font-weight: 600; letter-spacing: 0.26em; }
.concept-portrait { grid-column: 2; grid-row: 1 / 3; align-self: center; width: 100%; margin: 0; padding: 0.4rem 0.4rem 1.2rem; background: #f7f5f0; box-shadow: 0 6px 18px rgba(0,0,0,0.14); rotate: 5deg; scale: 1; transition: scale 350ms var(--move), transform 350ms var(--move), rotate 350ms var(--move); }
.concept-portrait { --site-default-cursor: var(--site-black-default-cursor); --site-interactive-cursor: var(--site-black-interactive-cursor); cursor: var(--site-default-cursor); pointer-events: auto; }
.concept-portrait-image { aspect-ratio: 0.86; overflow: hidden; background: #d3c5b8; }
.concept-portrait img { border-radius: 0; display: block; width: 100%; height: 100%; object-fit: cover; object-position: 50% 20%; }
.concept-intro > .concept-portrait-note { position: absolute; z-index: 4; top: calc(100% - var(--composition-unit) * 442.5 + 12px); right: calc(var(--composition-unit) * -44); width: calc(var(--composition-unit) * 287); }
.concept-portrait-note .concept-portrait { box-sizing: border-box; }
.concept-portrait-caption { container-type: inline-size; padding: clamp(7px, calc(var(--composition-unit) * 10), 12px) clamp(3px, calc(var(--composition-unit) * 6), 8px) clamp(4px, calc(var(--composition-unit) * 7), 8px); }
.concept-mobile-identity, .story-mobile-ask { display: none; }
.concept-answer { position: absolute; inset: 0; min-width: 0; opacity: 0; visibility: hidden; transform: translateY(24px); transition: opacity 250ms ease, transform 450ms var(--move), visibility 0s 450ms; }
.concept-turn-enter-active,
.concept-turn-leave-active { transition: opacity 220ms ease, transform 350ms var(--move); }
.concept-turn-enter-from { opacity: 0; transform: translateY(12px); }
.concept-turn-leave-to { opacity: 0; transform: translateY(-8px); }
.concept-response-enter-active,
.concept-response-leave-active { transition: opacity 180ms ease, transform 250ms var(--move); }
.concept-response-enter-from { opacity: 0; transform: translateY(6px); }
.concept-response-leave-to { opacity: 0; transform: translateY(-4px); }
.concept-answer-scroll { height: 100%; min-width: 0; overflow: auto; overscroll-behavior-y: auto; padding: 0.5rem 0.8rem 1rem 0; scrollbar-width: thin; scrollbar-color: var(--line) transparent; }
.concept-question { margin: 0 0 1.5rem; font-size: clamp(1.2rem,calc(var(--hero-unit) * 2.1),1.7rem); font-weight: 600; letter-spacing: -0.025em; line-height: 1.35; overflow-wrap: anywhere; }
.concept-answer-copy { max-width: 65ch; margin: 0; font-family: "PT Serif", Georgia, serif; font-size: 0.98rem; line-height: 1.75; white-space: pre-line; overflow-wrap: anywhere; }
.concept-answer-copy :deep(.ask-inline-link) { color: inherit; text-decoration-color: var(--muted); text-underline-offset: 0.2em; }
.concept-answer-scroll > :deep(.ask-audio) { margin: 1rem 0 0; }
.concept-answer-scroll > :deep(.ask-actions) { margin-top: 1rem; }
.concept-answer-scroll > :deep(.compose-progress),
.concept-answer-scroll > :deep(.sp-ask-view-offer) { margin-top: 1.25rem; }
.concept-ask :disabled, .concept-answer-followup:disabled { cursor: default; opacity: 0.5; }
.concept-answer-error { margin: 0; color: var(--muted); line-height: 1.6; }
.concept-thinking { display: flex; align-items: center; gap: 0.65rem; color: var(--muted); }
.concept-thinking span { flex: 0 0 auto; width: 0.4rem; height: 0.4rem; border-radius: 50%; background: currentColor; animation: concept-pulse 900ms ease-in-out infinite alternate; }
.concept-thinking p { margin: 0; line-height: 1.5; }
.home-ask-map-shell { height: 190px; margin-top: 0.8rem; overflow: hidden; }
.home-ask-map,
.home-ask-map :deep(.emap) { height: 100%; min-height: 0; }
.home-ask-map :deep(.emap) { display: flex; flex-direction: column; }
.home-ask-map :deep(.emap-stage) { flex: 1 1 auto; min-height: 0; border: 0; border-radius: 0; background: transparent; }
.concept-answer-sources-label,
.concept-answer-followups-label {
  display: block;
  margin: 0.8rem 0 0.4rem;
  color: #aaa;
  font: 400 0.62rem/1.4 "Quattrocento Sans", sans-serif;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}
.concept-answer-sources { display: flex; min-width: 0; flex-wrap: wrap; gap: 6px 12px; }
.concept-answer-followups-list { display: flex; min-width: 0; flex-wrap: wrap; gap: 0.42rem; }
.concept-answer-followup { min-width: 0; max-width: 100%; white-space: normal; overflow-wrap: anywhere; }
.concept-answer-sources-wrap { position: relative; }
/* Match the Palette's 14px card margin, 4px source inset and 8px label inset. */
.concept-answer-scroll > :deep(:is(.sp-ask-view-offer, .compose-progress)) + :is(.concept-answer-sources-wrap, .concept-answer-followups) { padding-top: 26px; }
.concept-answer-scroll > :deep(:is(.sp-ask-view-offer, .compose-progress)) + :is(.concept-answer-sources-wrap, .concept-answer-followups) :is(.concept-answer-sources-label, .concept-answer-followups-label) { margin-top: 0; }
.home-ask-preview {
  position: fixed;
  z-index: 9100;
  box-sizing: border-box;
  padding: 0.65rem 0.75rem;
  border: 1px solid var(--home-hairline);
  background: rgba(250, 250, 251, 0.96);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.1);
  pointer-events: none;
}
.home-ask-preview-kicker { display: block; overflow: hidden; color: var(--home-muted); font: 600 0.56rem/1.2 sans-serif; letter-spacing: 0.1em; text-overflow: ellipsis; text-transform: uppercase; white-space: nowrap; }
.home-ask-preview-text { margin: 0.25rem 0 0; font-size: 0.72rem; line-height: 1.4; }
.concept-ask { position: relative; min-width: 0; }
.concept-sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; border: 0; }
.concept-ask-field { position: relative; display: grid; grid-template-columns: minmax(0,1fr) auto; align-items: center; gap: 0.75rem; min-height: 3.5rem; border-bottom: 1px solid var(--line); }
.concept-ask-field::before { content: ""; position: absolute; bottom: -1px; left: 0; right: 0; height: 1px; background: var(--ink); opacity: 0; pointer-events: none; transition: opacity 220ms ease; }
.concept-ask input { min-width: 0; width: 100%; min-height: 3.5rem; padding: 0.6rem 0; border: 0; border-radius: 0; outline: 0; background: transparent; color: var(--ink); font: inherit; font-size: 1rem; }
.concept-ask input::placeholder { color: var(--muted); opacity: 1; }
@media (min-width: 761px) {
  .concept-ask-field { isolation: isolate; cursor: var(--site-text-cursor); }
  .concept-ask-field::after { content: ''; position: absolute; z-index: -1; inset: 0; border-radius: 3px 3px 0 0; background: color-mix(in srgb, var(--ink) 2.5%, transparent); opacity: 0; transform: scaleY(.65); transform-origin: bottom; pointer-events: none; transition: opacity 220ms ease, transform 320ms cubic-bezier(.22,1,.36,1); }
  .concept-ask input { box-sizing: border-box; }
  .concept-ask-field:focus-within::after { opacity: 1; transform: scaleY(1); }
  .concept-ask-field:focus-within::before { opacity: .75; }
  .concept-ask-field:focus-within { outline: none; }
}
@media (min-width: 761px) and (hover: hover) and (pointer: fine) {
  .concept-ask-field:hover::after { opacity: 1; transform: scaleY(1); }
  .concept-ask-field:hover::before { opacity: .75; }
}

.concept-ask button { display: grid; width: 2.5rem; height: 2.5rem; padding: 0; border: 0; place-items: center; background: transparent; color: var(--ink); font: inherit; font-size: 1.5rem; cursor: pointer; transition: opacity 160ms ease, transform 200ms var(--move); }
.concept-ask button:disabled { opacity: 0.3; cursor: default; }

.is-engaged .concept-frame { grid-template-columns: minmax(0,1fr) minmax(0,23.203125%); }
@media (min-width: 761px) {
  .is-engaged .concept-scene { justify-content: center; }
}
.is-engaged .concept-card { padding: clamp(1.5rem,calc(var(--hero-unit) * 3.2),3rem); background: var(--surface); }
.is-engaged .concept-intro { opacity: 0; visibility: hidden; transform: translateY(-24px); transition: opacity 240ms ease, transform 500ms var(--move), visibility 0s 500ms; }
.is-engaged .concept-answer { opacity: 1; visibility: visible; transform: translateY(0); transition: opacity 350ms ease 150ms, transform 600ms var(--move) 100ms, visibility 0s; }
.is-engaged .concept-return { opacity: 1; visibility: visible; transform: translateX(0); transition-delay: 160ms, 100ms, 0s; }
.story-scroll-cue { display: grid; justify-items: center; align-self: center; gap: 0.35rem; min-width: 12rem; min-height: 3rem; padding: 0.35rem 1rem; border: 0; background: transparent; color: var(--muted); font: inherit; cursor: pointer; }
.story-next-label { font-size: 0.6rem; letter-spacing: 0.12em; text-transform: uppercase; }
.story-next-title { font: 500 1.35rem/1.1 "Cormorant Garamond",Georgia,serif; color: var(--ink); opacity: 0.7; transition: opacity 200ms ease, transform 350ms var(--move); }
.story-rail { position: fixed; z-index: 25; right: 1rem; top: 50%; display: grid; gap: 0.35rem; transform: translateY(-50%); }
.story-rail button { position: relative; display: flex; align-items: center; justify-content: flex-end; gap: 0.65rem; min-width: 2rem; min-height: 2rem; padding: 0; border: 0; background: transparent; color: var(--muted); font: inherit; cursor: pointer; }
.story-rail-mark { display: grid; place-items: center; width: 1.8rem; height: 1.8rem; border-radius: 50%; font-size: 0.6rem; font-variant-numeric: tabular-nums; transition: color 220ms ease, background-color 220ms ease, transform 400ms var(--move); }
.story-rail-label { position: absolute; top: 50%; right: calc(100% + 0.55rem); box-sizing: border-box; display: grid; align-content: center; gap: 0.16rem; width: 9.75rem; min-height: 2.9rem; padding: 0.48rem 0.65rem 0.52rem; background: var(--page-background); color: var(--ink); text-align: left; opacity: 0; visibility: hidden; translate: 0 -50%; transform: translateX(6px); transition: opacity 160ms ease, visibility 0s 160ms, transform 180ms cubic-bezier(.23,1,.32,1); pointer-events: none; }
.story-rail-label-section { color: var(--muted); font: 600 0.55rem/1.15 "Quattrocento Sans", sans-serif; letter-spacing: 0.12em; text-transform: uppercase; }
.story-rail-label-title { font: 500 1.18rem/1.05 "Cormorant Garamond", Georgia, serif; letter-spacing: -0.015em; }
.story-rail button[aria-current="step"] .story-rail-mark { background: var(--ink); color: var(--surface); transform: scale(1.08); }
.home-concept-page .story-rail button:focus-visible { outline: none !important; }
.story-rail button:focus-visible .story-rail-label { opacity: 1; visibility: visible; transform: none; transition: none; }
.story-rail button:focus-visible .story-rail-mark { outline: 2px solid var(--ink); outline-offset: 3px; }
.home-concept-page :deep(#story-contact #videoElement) { cursor: var(--site-default-cursor, default); }
@media (prefers-reduced-motion: reduce) {
  .story-rail-label { transform: none; transition: none; }
}
.story-now { display: grid; grid-template-columns: 0.9fr 1.1fr; align-items: center; gap: 4rem; padding-block: clamp(2rem, 7svh, 6rem); }
.story-eyebrow { margin: 0 0 1.5rem; color: var(--muted); font-size: 0.7rem; letter-spacing: 0.1em; text-transform: uppercase; }
.story-now h2 { margin: 0; font: 500 var(--home-heading-size, clamp(3rem,5.2vw,5.5rem))/1.02 "Cormorant Garamond", Georgia, serif; letter-spacing: -0.04em; }
.concept-current { display: grid; min-width: 0; }
.concept-current a { position: relative; isolation: isolate; display: flex; align-items: center; gap: 1.5rem; min-width: 0; min-height: clamp(5rem, calc((var(--story-page-height) - 14svh) / 4), 9rem); padding-block: 1.1rem; border-top: 1px solid var(--line); color: inherit; text-decoration: none; }
.concept-current a::before { content: ""; position: absolute; z-index: -1; inset: -1px -1rem; background: var(--ink); opacity: 0; transform: scaleY(0.94); pointer-events: none; transition: opacity 220ms ease, transform 280ms var(--move); }
.concept-current a:last-child { border-bottom: 1px solid var(--line); }
.concept-current-number { align-self: flex-start; flex-shrink: 0; margin-top: 0.2rem; color: var(--faint); font-size: 0.67rem; font-variant-numeric: tabular-nums; transition: color 220ms ease, transform 280ms var(--move); }
.concept-current-copy { display: grid; flex: 1; gap: 0.6rem; min-width: 0; padding-right: 0.5rem; transition: transform 280ms var(--move); }
.concept-current-copy small { color: var(--muted); font-size: 0.7rem; }
.concept-current-copy strong { font-size: clamp(1.2rem,2cqw,1.6rem); font-weight: 500; line-height: 1.3; }
.concept-current a > svg { width: 1rem; height: 1rem; flex-shrink: 0; transition: transform 280ms var(--move); }
.concept-current a:focus-visible::before { opacity: 0.07; transform: scaleY(1); }
.concept-current a:focus-visible .concept-current-copy { transform: translateX(8px); }
.concept-current a:focus-visible .concept-current-number { color: var(--ink); transform: translateX(4px); }
.concept-current a:focus-visible > svg { transform: translate(3px,-3px); }
.story-index { display: grid; align-content: center; gap: clamp(2.5rem,5cqw,4rem); min-height: calc(var(--story-page-height) - var(--story-footer-space,9rem)); padding-block: clamp(4rem,8vh,7rem); border-top: 1px solid var(--line); scroll-margin-top: var(--story-top); }
.story-index > header { display: flex; align-items: end; justify-content: space-between; gap: 2rem; }
.story-index h2 { margin: 0; font: 500 var(--home-index-heading-size, clamp(3rem,5.5cqw,5rem))/0.98 "Cormorant Garamond",Georgia,serif; letter-spacing: -0.045em; }
.story-index-home { display: inline-flex; align-items: center; gap: 0.75rem; padding: 0.75rem 0; border: 0; background: transparent; color: var(--muted); font: inherit; cursor: pointer; }
.story-index-home span { transition: transform 240ms var(--move); }
.story-index-home:focus-visible span { transform: translateY(-4px); }
.story-index nav { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 0 clamp(2rem,5cqw,4rem); }
.story-index a { display: flex; align-items: center; min-width: 0; padding: clamp(1.5rem,3cqw,2.5rem) 0; border-bottom: 1px solid var(--line); color: inherit; font: clamp(1.65rem,3.2cqw,2.75rem)/1.15 "PT Serif",Georgia,serif; text-decoration: none; }
.story-index a > span { transition: transform 240ms var(--move); }
.story-index a:focus-visible > span { transform: translateX(4px); }
.home-concept-page a:focus-visible,
.home-concept-page button:focus-visible,
.concept-answer-scroll:focus-visible { outline: 2px solid var(--ink); outline-offset: 4px; }
.home-concept-page button:not(:disabled):not(.concept-scene-sound):active { transform: scale(0.97); }
.concept-scene-sound svg { transition: transform 130ms ease; }
.concept-scene-sound:active svg { transform: scale(0.9); }
@keyframes concept-pulse { from { opacity: 0.3; } to { opacity: 1; } }
@keyframes answer-details { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@media (hover: hover) and (pointer: fine) {
  .story-index-home:hover span { transform: translateY(-4px); }
  .story-index a:hover > span { transform: translateX(4px); }
  .concept-return:hover span { transform: translateX(-4px); }
  .concept-context a:hover { color: var(--ink); transform: translateY(-2px); }
  .concept-portrait:hover { rotate: 0deg; scale: 1.05; transform: translateY(-3px); }
  .concept-current a:hover > svg { transform: translate(3px,-3px); }
  .concept-current a:hover::before { opacity: 0.07; transform: scaleY(1); }
  .concept-current a:hover .concept-current-copy { transform: translateX(8px); }
  .concept-current a:hover .concept-current-number { color: var(--ink); transform: translateX(4px); }
  .concept-scene-sound:hover { opacity: 1; }
  .concept-ask button:not(:disabled):hover { transform: translateX(3px); }
  .story-scroll-cue:hover .story-next-title { opacity: 1; transform: translateY(-2px); }
  .story-rail button:hover .story-rail-label { opacity: 1; visibility: visible; transform: none; transition-delay: 0s; }
  .story-rail:has(button:focus-visible) button:hover:not(:focus-visible) .story-rail-label { opacity: 0; visibility: hidden; }
  .story-rail button:hover .story-rail-mark { background: var(--ink); color: var(--surface); }
  .story-rail button[aria-current="step"]:hover .story-rail-mark { color: var(--surface); }
}
@media (hover: none), (pointer: coarse) {
  .story-index-home:active span { transform: translateY(-4px); }
  .story-index a:active > span { transform: translateX(4px); }
  .concept-return:active span { transform: translateX(-4px); }
  .concept-context a:active { color: var(--ink); transform: translateY(-2px); }
  .concept-portrait:active { rotate: 0deg; scale: 1.05; transform: translateY(-3px); }
  .concept-current a:active > svg { transform: translate(3px,-3px); }
  .concept-current a:active::before { opacity: 0.07; transform: scaleY(1); }
  .concept-current a:active .concept-current-copy { transform: translateX(8px); }
  .concept-current a:active .concept-current-number { color: var(--ink); transform: translateX(4px); }
  .concept-scene-sound:active { opacity: 1; }
  .concept-ask button:not(:disabled):active { transform: translateX(3px); }
  .story-scroll-cue:active .story-next-title { opacity: 1; transform: translateY(-2px); }
}
:global(body.dark-mode .home-concept-page) { --ink: #eeebe4; --muted: #b1aea8; --faint: #96938e; --line: rgba(238,235,228,0.17); --surface: #232323; --page-background: var(--dark-bg, #1c1c1e); }
/* A smaller composition inside each full-height chapter gives desktop screens
   the breathing room of 90% zoom without scaling scroll coordinates or controls. */
@media (min-width: 761px) {
  .home-concept-page {
    --home-page-measure: 70.2rem;
    --home-hero-measure: 57.6rem;
    --home-hero-height-measure: calc(max(21.6rem, 90svh - 11.7rem) * 1024 / 594);
    --home-heading-size: clamp(2.7rem, min(4.68vw, 8.6svh), 4.95rem);
    --home-index-heading-size: clamp(2.7rem, min(5.5cqw, 8.6svh), 4.5rem);
    --home-copy-size: clamp(1rem, 1.08vw, 1.01rem);
  }
  .story-now { gap: clamp(2rem, 4.5vw, 3.6rem); }
  .concept-current a { min-height: clamp(4.5rem, calc((var(--story-page-height) - 20svh) / 4), 8.1rem); }
  .concept-current-copy strong { font-size: clamp(1.1rem, 2cqw, 1.44rem); }
  .story-index a { padding-block: clamp(1.35rem, 3cqw, 2.25rem); font-size: clamp(1.5rem, 3.2cqw, 2.475rem); }
}
@container concept (max-width: 900px) {
  .story-now { gap: 2rem; }
  .story-index nav { grid-template-columns: repeat(2,minmax(0,1fr)); }
}
@media (max-width: 760px) {
  .home-concept-page { --page-background: #fff; width: 100vw; margin: calc(-48px - 0.82rem) 0 0 calc(50% - 50vw); }
  :global(body.dark-mode .home-concept-page) { --page-background: var(--dark-bg, #1c1c1e); }
  .story-rail, .story-scroll-cue { display: none; }
  .story-hero {
    --site-default-cursor: var(--site-white-default-cursor);
    --site-interactive-cursor: var(--site-white-interactive-cursor);
    cursor: var(--site-default-cursor);
  }
  .story-hero { position: relative; overflow: clip; min-height: 620px; height: auto; padding: 0; background: #1c1c1e; }
  .concept-stage { width: 100%; height: auto; container-type: normal; }
  .concept-frame { position: relative; display: block; width: 100%; height: auto; }
  .concept-scene { position: absolute; inset: 0; height: 100%; }
  .concept-media { width: 100%; height: 100%; aspect-ratio: auto; }
  .concept-media::after { content: ''; position: absolute; inset: 0; pointer-events: none; background: linear-gradient(90deg,rgba(8,11,14,0.4) 0%,rgba(8,11,14,0.4) 52%,rgba(8,11,14,0.04)), linear-gradient(180deg,rgba(8,8,10,0.48),rgba(8,8,10,0.36) 48%,rgba(8,8,10,0.24) 65%,rgba(8,8,10,0.65)); }
  .concept-video, .concept-video-poster { object-fit: cover; object-position: center; }
  /* The portrait belongs to the introduction. A flexible third row leaves
     the coast visible and holds both affiliations on one quiet baseline. */
  .concept-mobile-identity { --mobile-columns: minmax(0,1fr) minmax(0,0.75fr); --mobile-gutter: clamp(20px,6vw,28px); position: relative; z-index: 4; display: grid; grid-template-rows: auto auto minmax(2rem,1fr) auto; width: min(100%,36rem); min-height: max(590px,calc(100svh + 34px)); margin-inline: auto; padding: clamp(6rem,16svh,9rem) clamp(24px,7vw,38px) clamp(4.5rem,12svh,7.5rem); color: #f5f3ed; pointer-events: none; }
  .concept-mobile-identity h1 { grid-row: 1; display: grid; margin: 0; font: 500 clamp(5.2rem,min(28vw,13.5svh),7.5rem)/0.84 "Cormorant Garamond",Georgia,serif; letter-spacing: -0.06em; color: inherit; transition: color 420ms ease; }
  .concept-mobile-intro { grid-row: 2; display: grid; grid-template-columns: var(--mobile-columns); align-items: center; gap: var(--mobile-gutter); margin-top: clamp(1.75rem,5svh,3rem); }
  .concept-mobile-identity .concept-credo { grid-column: 1; grid-row: 1; gap: 0.16rem; margin: 0; font: 450 clamp(1.125rem,5.35vw,1.375rem)/1.55 -apple-system,BlinkMacSystemFont,"SF Pro Text","Helvetica Neue",sans-serif; letter-spacing: -0.025em; color: rgba(245,243,237,0.92); transition: color 420ms ease; }
  .concept-mobile-identity .concept-portrait { grid-column: 2; grid-row: 1; position: relative; top: 8px; width: 100%; margin: 0; padding: 0.28rem; rotate: 4deg; }
  .concept-mobile-identity .concept-portrait-caption { padding: 2px 0; pointer-events: auto; }
  .concept-mobile-identity .concept-context { grid-row: 4; display: grid; grid-template-columns: var(--mobile-columns); align-items: start; gap: var(--mobile-gutter); margin: 0; padding-top: 1.15rem; border-top: 1px solid rgba(245,243,237,0.22); pointer-events: auto; }
  .concept-mobile-identity .concept-context a { display: flex; flex-direction: column; align-items: flex-start; justify-content: flex-start; gap: 0.38rem; min-height: 44px; color: rgba(245,243,237,0.7); line-height: 1.3; }
  .concept-mobile-identity .concept-context a span { font-size: 0.75rem; letter-spacing: 0.01em; text-transform: none; }
  .concept-mobile-identity .concept-context a strong { color: #f5f3ed; font-size: 0.9rem; font-weight: 600; letter-spacing: 0.12em; transition: color 420ms ease; }
  .concept-mobile-identity a:focus-visible { outline-color: currentColor; }
  .concept-scene-mask { background: #1c1c1e; color: #f5f5f7; }
  .concept-scene-mask span {
    position: absolute;
    top: calc(4.75rem + env(safe-area-inset-top, 0px));
    right: clamp(4px, calc(8vw - 22px), 12px);
    bottom: auto;
    left: auto;
    writing-mode: vertical-rl;
    text-orientation: sideways;
    font-size: 0.65625rem;
    letter-spacing: 0.09em;
    /* Turn the whole line so its baseline faces the right edge. */
    transform: translateX(26px) rotate(180deg);
    transition: opacity 220ms ease, transform 380ms cubic-bezier(0.4, 0, 0.6, 1);
  }
  .concept-scene-mask.is-visible span {
    opacity: 0.78;
    transform: translateX(0) rotate(180deg);
    transition-duration: 320ms, 620ms;
    transition-timing-function: ease, cubic-bezier(0.22, 1, 0.36, 1);
    transition-delay: 160ms;
  }
  .story-now { position: relative; z-index: 3; min-height: auto; grid-template-columns: minmax(0,1fr); align-content: center; gap: 3rem; margin-top: 0; padding: 6rem clamp(24px,7vw,38px); background: var(--page-background); }
  .story-now h2 { font-size: 3.3rem; }
  .story-index { min-height: auto; grid-template-columns: minmax(0,1fr); gap: 2.5rem; margin-inline: clamp(24px,7vw,38px); padding-block: 4rem; }
  .story-index h2 { font-size: 3.3rem; }
  .story-index nav { grid-template-columns: minmax(0,1fr); }
  .story-index a { padding-block: 0.72rem; }
  .concept-current a { min-height: 7rem; }
  .story-mobile-ask { position: relative; z-index: 3; display: flex; flex-direction: column; justify-content: flex-start; min-height: 0; margin-top: -50px; padding: 4rem clamp(24px,7vw,38px); border-radius: 34px 34px 0 0; background: var(--page-background); scroll-margin-top: 3rem; }
  .story-mobile-ask::after { content: ""; position: absolute; bottom: 0; left: clamp(24px,7vw,38px); right: clamp(24px,7vw,38px); height: 1px; background: var(--line); }
  .concept-card { display: flex; flex-direction: column; height: auto; padding: 0; border: 0; background: transparent; }
  .concept-card-header { display: none; }
  .concept-card-body { display: contents; }
  .concept-intro { order: 1; position: relative; inset: auto; display: flex; flex-direction: column; align-items: flex-start; justify-content: center; max-height: 18rem; overflow: visible; transition: max-height 440ms var(--move), opacity 260ms ease, transform 440ms var(--move), visibility 0s; }
  .concept-ask { order: 2; margin-top: 2rem; }
  .concept-ask-field {
    grid-template-columns: minmax(0,1fr) 44px;
    gap: 0 0.75rem;
    padding: 1.125rem;
    border: 1px solid var(--line);
    border-radius: 20px;
    background: var(--surface);
    height: 150px;
    grid-template-rows: minmax(0,1fr) 44px;
    box-shadow: none;
    -webkit-tap-highlight-color: transparent;
    transition: height 240ms var(--move), padding 240ms var(--move), border-color 180ms ease;
  }
  .concept-ask-field::before { display: none; }
  .concept-ask-field:focus-within {
    border-color: var(--muted);
    box-shadow: none;
  }
  .concept-ask textarea {
    grid-column: 1 / -1;
    grid-row: 1;
    align-self: start;
    display: block;
    width: 100%;
    min-width: 0;
    height: 100%;
    min-height: 0;
    appearance: none;
    -webkit-appearance: none;
    box-shadow: none;
    -webkit-tap-highlight-color: transparent;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 0;
    outline: 0;
    resize: none;
    background: transparent;
    color: var(--ink);
    caret-color: var(--ink);
    font: inherit;
    font-size: 1rem;
    line-height: 1.6;
  }
  .concept-ask.is-compact .concept-ask-field { height: 74px; padding: 14px 18px; }
  .concept-ask.is-compact textarea { grid-column: 1; grid-row: 1 / 3; align-self: center; height: 26px; }
  .concept-ask textarea::placeholder { color: var(--muted); opacity: 1; }
  .concept-ask button {
    grid-column: 2;
    grid-row: 1 / 3;
    align-self: end;
    -webkit-tap-highlight-color: transparent;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: var(--ink);
    color: var(--surface);
    transition: background-color 180ms ease, color 180ms ease, transform 160ms var(--move);
  }
  .concept-ask button:disabled { background: var(--line); color: var(--muted); opacity: 1; }
  .concept-ask button svg { width: 22px; height: 22px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
  .concept-answer { order: 3; inset: auto; margin-top: clamp(1.4rem,5vw,2rem); }
  .concept-answer-scroll { height: auto; max-height: none; overflow: visible; padding: 0; overscroll-behavior: auto; }
  .is-engaged .story-mobile-ask { justify-content: flex-start; }
  .is-engaged .concept-intro { max-height: 0; overflow: clip; transform: translateY(-48px) scale(0.965); transition: max-height 440ms var(--move), opacity 260ms ease, transform 440ms var(--move), visibility 0s 440ms; }
  .is-engaged .concept-ask { margin-top: 0.5rem; }
  .is-engaged .concept-answer { position: relative; }

  .concept-mobile-ask-intro h2 { max-width: 10ch; margin: 0 0 1.25rem; font: 500 3.4rem/1.04 "Cormorant Garamond",Georgia,serif; letter-spacing: -0.04em; }
  .concept-mobile-ask-intro h2 :deep(.ai-ask-term) { font: inherit; }
  .concept-mobile-ask-intro p { margin: 0; color: var(--muted); line-height: 1.7; }
  .is-engaged .concept-card { padding: 0; background: transparent; }
}
@media (max-width: 380px), (max-height: 680px) and (max-width: 760px) {
  .story-hero { min-height: 590px; }
}
@media (prefers-reduced-motion: reduce) {
  .home-concept-page *,
  .home-concept-page *::before,
  .home-concept-page *::after { animation: none !important; transition: none !important; }
  .concept-intro,
  .concept-answer,
  .is-engaged .concept-intro,
  .is-engaged .concept-answer { transform: none; }
}

.home-preview-fade-enter-active, .home-preview-fade-leave-active { transition: opacity 120ms ease; }
.home-preview-fade-enter-from, .home-preview-fade-leave-to { opacity: 0; }
:global(body.dark-mode .home-ask-preview) { color: #eeeef0; border-color: rgba(255,255,255,0.15); background: rgba(44,44,46,0.97); }
</style>

<style src="../assets/css/home-story.css"></style>
